import { useContext, useMemo, useEffect } from 'react';
import InlineOptions from '../../inline-options';
import {
  MetricEnum,
  VolatilityConesContext,
  WindowEnum,
} from '../../../../providers/volatility-cones-provider';
import Chart from '../../chart';
import './styles.scss';

import Divider from '../../divider';
import { periodOptions, tenorOptions } from '../../constants/volatility-options';
import usePersistentState from '../../../../providers/use-persistent-state';
import { TimestampCalendarPicker } from '../../calendar/timestamp-calendar-picker';

const dashedLine = [4, 8];

export const VOL_CONES_SERIES = [
  { key: 'current', color: '#26D9D9', lineType: 'solid' },
  { key: 'high', color: '#FAA638', lineType: dashedLine },
  { key: 'median', color: '#F3733F', lineType: dashedLine },
  { key: 'low', color: '#ABBD75', lineType: dashedLine },
  { key: 'p75', color: '#806CE5', lineType: dashedLine },
  { key: 'p25', color: '#F589D5', lineType: dashedLine },
];

const LEGEND_LABELS: Record<string, string> = {
  current: 'Current',
  high: 'Max',
  median: 'Median',
  low: 'Min',
  p75: '75th Percentile',
  p25: '25th Percentile',
};

const RELOAD_INTERVAL_MS = 60 * 1000; // 1 min

const VolatilityConesContent = ({ providerKey }: { providerKey: string }) => {
  const {
    data,
    isLoading,
    selectedPeriod,
    setSelectedPeriod,
    selectedTenor,
    setSelectedTenor,
    lastTimestamp,
    isLive,
    resetToLive,
    setHistoricalDate,
  } = useContext(VolatilityConesContext);

  const [legendSelected, setLegendSelected] = usePersistentState<Record<string, boolean>>(
    [providerKey, 'VOL-CONES'],
    'legendSelected',
    {},
  );

  const renderInlineOptions = () => (
    <div className="volatility-cones-header">
      <InlineOptions
        options={periodOptions}
        selectedOption={selectedPeriod}
        onOptionChange={(val) => setSelectedPeriod(val as WindowEnum)}
        maxVisibleOptions={5}
        leftLabel="Lookback Window"
      />
      <Divider />
      <InlineOptions
        options={tenorOptions}
        selectedOption={selectedTenor}
        onOptionChange={(val) => {
          if (Array.isArray(val) && val.length >= 0) {
            setSelectedTenor(val[0] as MetricEnum);

            return;
          }

          setSelectedTenor(val as MetricEnum);
        }}
        maxVisibleOptions={5}
      />
    </div>
  );
  const xAxis = useMemo(() => {
    if (!data || data.length === 0)
      return { type: 'category' as const, data: [], boundaryGap: false };

    return {
      type: 'category' as const,
      data: data.map((item) => item.tenor) as any[],
      boundaryGap: false,
    };
  }, [data]);

  const series = useMemo(() => {
    if (!data || data.length === 0) return [];

    return VOL_CONES_SERIES.map(({ key, color, lineType }) => ({
      type: 'line' as const,
      data: data.map((item: any) => item[key as any]),
      color: color,
      name: LEGEND_LABELS[key],
      key,
      symbolSize: 6,
      symbol: 'circle',
      lineStyle: {
        type: lineType,
      },
    }));
  }, [data]);

  const yAxis = useMemo(
    () => ({
      type: 'value' as const,
      axisLabel: {
        align: 'left',
        formatter: (value: any) => `${(value * 100).toFixed(0)}%`,
      },
    }),
    [],
  );

  const handleApply = (newTimestamp: string) => {
    if (!newTimestamp) return;

    setHistoricalDate(newTimestamp);
  };

  const handleReset = () => {
    resetToLive();
  };

  useEffect(() => {
    if (!isLive) return;

    const interval = setInterval(() => {
      resetToLive();
    }, RELOAD_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isLive, resetToLive]);

  return (
    <div className="volatility-cones-container">
      {renderInlineOptions()}
      <div className="volatility-cones-timestamp">
        <TimestampCalendarPicker
          handleChangeDatetime={handleApply}
          isLive={isLive}
          reset={handleReset}
          displayTimestamp={lastTimestamp}
        />
      </div>
      <Chart
        series={series}
        xAxis={xAxis}
        yAxis={yAxis}
        tooltip={{
          valueFormatter: (value: any) => `${(value * 100).toFixed(2)}%`,
        }}
        grid={{ right: 5, left: 15 }}
        className="volatility-cones-chart"
        isLoading={isLoading}
        legend={{
          selected: legendSelected,
        }}
        onLegendSelectChanged={(payload) => setLegendSelected(payload.selected || {})}
      />
    </div>
  );
};

export default VolatilityConesContent;
